import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import portalT from '../translations/portal';
import axios from 'axios';
import {
  Crown, TrendingUp, Megaphone, Cog, Calculator, Users, Code, Headphones,
  Loader, ArrowRight, Sparkles, CheckCircle, Compass,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const ROLE_ICONS = { crown: Crown, 'trending-up': TrendingUp, megaphone: Megaphone, cog: Cog, calculator: Calculator, users: Users, code: Code, headphones: Headphones };

const OnboardingFlow = ({ onComplete }) => {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = portalT[lang];
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [roles, setRoles] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [companySize, setCompanySize] = useState('1-50');
  const [challenge, setChallenge] = useState('');
  const [result, setResult] = useState(null);

  const l = lang === 'nl' ? {
    welcome: 'Welkom bij Krypton9!', welcomeSub: 'Laten we je AI-toolkit personaliseren. Dit duurt maar 30 seconden.',
    step1: 'Wat is je rol?', step2: 'In welke branche werk je?', step3: 'Vertel over je grootste uitdaging',
    challengePlaceholder: 'Bijv. We verliezen te veel tijd aan handmatige processen...',
    sizeLabel: 'Bedrijfsgrootte', next: 'Volgende', generating: 'Je plan maken...',
    yourPlan: 'Je Persoonlijk AI Plan', recommendedTools: 'Aanbevolen tools voor jou:',
    actionPlan: 'Je Actieplan', startExploring: 'Aan de Slag', skip: 'Overslaan',
  } : {
    welcome: 'Welcome to Krypton9!', welcomeSub: "Let's personalize your AI toolkit. This takes 30 seconds.",
    step1: 'What is your role?', step2: 'What industry are you in?', step3: 'Tell us about your biggest challenge',
    challengePlaceholder: 'E.g., We lose too much time on manual processes...',
    sizeLabel: 'Company size', next: 'Next', generating: 'Creating your plan...',
    yourPlan: 'Your Personalized AI Plan', recommendedTools: 'Recommended tools for you:',
    actionPlan: 'Your Action Plan', startExploring: 'Start Exploring', skip: 'Skip for now',
  };

  useEffect(() => {
    axios.get(`${API}/onboarding/options`)
      .then(r => {
        setRoles(r.data?.roles || []);
        setIndustries(r.data?.industries || []);
      })
      .catch(e => { console.error('Onboarding options failed:', e); })
      .finally(() => setLoading(false));
  }, []);

  const completeOnboarding = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/onboarding/complete`, { role: selectedRole, industry: selectedIndustry, company_size: companySize, main_challenge: challenge }, { headers: { Authorization: `Bearer ${token}` } });
      setResult(res.data); setStep(4);
    } catch (e) { console.error(e); } finally { setSubmitting(false); }
  };

  if (loading) return <div className="onboarding-overlay"><Loader className="spin" size={32} /></div>;

  return (
    <div className="onboarding-overlay" data-testid="onboarding-flow">
      <div className="onboarding-card">
        <div className="onboarding-progress">{[1,2,3,4].map(s => <div key={s} className={`onboarding-step-dot ${step >= s ? 'active' : ''}`} />)}</div>

        {step === 1 && (
          <div className="onboarding-step" data-testid="onboarding-step-1">
            <Sparkles size={28} className="onboarding-icon" />
            <h2 className="heading-2">{l.welcome}</h2>
            <p className="body-medium text-secondary">{l.welcomeSub}</p>
            <h3 className="heading-3" style={{ marginTop: '1.5rem' }}>{l.step1}</h3>
            <div className="onboarding-options-grid">
              {(roles || []).map(r => { const Icon = ROLE_ICONS[r.icon] || Users; return (
                <button key={r.id} className={`onboarding-option ${selectedRole === r.id ? 'selected' : ''}`} onClick={() => setSelectedRole(r.id)} data-testid={`role-${r.id}`}>
                  <Icon size={20} /><span>{lang === 'nl' ? r.label_nl : r.label_en}</span>
                </button>
              ); })}
            </div>
            <div className="onboarding-actions">
              <button className="btn-secondary" onClick={onComplete} data-testid="skip-onboarding">{l.skip}</button>
              <button className="btn-primary" disabled={!selectedRole} onClick={() => setStep(2)} data-testid="next-step-1">{l.next} <ArrowRight size={16} /></button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="onboarding-step" data-testid="onboarding-step-2">
            <h3 className="heading-2">{l.step2}</h3>
            <div className="onboarding-options-grid industry-grid">
              {(industries || []).map(i => (
                <button key={i.id} className={`onboarding-option ${selectedIndustry === i.id ? 'selected' : ''}`} onClick={() => setSelectedIndustry(i.id)} data-testid={`industry-${i.id}`}>
                  <span>{lang === 'nl' ? i.label_nl : i.label_en}</span>
                </button>
              ))}
            </div>
            <div className="onboarding-actions">
              <button className="btn-secondary" onClick={() => setStep(1)}>&larr;</button>
              <button className="btn-primary" disabled={!selectedIndustry} onClick={() => setStep(3)} data-testid="next-step-2">{l.next} <ArrowRight size={16} /></button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="onboarding-step" data-testid="onboarding-step-3">
            <h3 className="heading-2">{l.step3}</h3>
            <textarea className="form-input" rows={4} placeholder={l.challengePlaceholder} value={challenge} onChange={e => setChallenge(e.target.value)} data-testid="challenge-input" />
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label className="body-small">{l.sizeLabel}</label>
              <select className="form-input" value={companySize} onChange={e => setCompanySize(e.target.value)} data-testid="size-select">
                <option value="1-10">1-10</option><option value="11-50">11-50</option><option value="51-200">51-200</option><option value="200+">200+</option>
              </select>
            </div>
            <div className="onboarding-actions">
              <button className="btn-secondary" onClick={() => setStep(2)}>&larr;</button>
              <button className="btn-primary" onClick={completeOnboarding} disabled={submitting} data-testid="complete-onboarding">
                {submitting ? <><Loader size={16} className="spin" /> {l.generating}</> : <><Sparkles size={16} /> {l.next}</>}
              </button>
            </div>
          </div>
        )}

        {step === 4 && result && (
          <div className="onboarding-step" data-testid="onboarding-step-4">
            <CheckCircle size={32} style={{ color: '#10b981' }} />
            <h2 className="heading-2">{l.yourPlan}</h2>
            <p className="body-medium">{result.profile?.welcome_message}</p>
            <div className="onboarding-recs" data-testid="onboarding-recommendations">
              <h4 className="body-small" style={{ fontWeight: 600, marginBottom: '0.75rem' }}>{l.recommendedTools}</h4>
              {(result.recommendations || []).map(rec => (
                <button key={rec.id} className="onboarding-rec-item" onClick={() => { onComplete(); navigate(rec.path); }} data-testid={`rec-tool-${rec.id}`}>
                  <Compass size={16} /><span>{t.tools[rec.id]?.title || rec.id}</span><ArrowRight size={14} />
                </button>
              ))}
            </div>
            {(result.profile?.action_steps || []).length > 0 && (
              <div className="onboarding-actions-list">
                <h4 className="body-small" style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{l.actionPlan}</h4>
                {(result.profile?.action_steps || []).map((s, i) => <div key={i} className="onboarding-action-item"><span className="onboarding-step-num">{i+1}</span><span className="body-small">{s}</span></div>)}
              </div>
            )}
            <button className="btn-primary" style={{ width: '100%', marginTop: '1.5rem' }} onClick={() => { onComplete(); navigate('/portal/billing'); }} data-testid="start-exploring">{l.startExploring} <ArrowRight size={16} /></button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingFlow;
